'use client'

import { User, Mail, CalendarDays, Shield, CreditCard } from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { ThemeToggle } from '@/components/ui/theme-toggle'
import { DashboardData, formatCredits, formatDate } from '../types'

interface Props {
  data: DashboardData
  onRecharge: () => void
}

function Row({ icon, label, value }: { icon: React.ReactNode; label: string; value: React.ReactNode }) {
  return (
    <div className="flex items-center gap-3 px-4 py-3 border-b border-tq-border last:border-0">
      <div className="text-tq-green shrink-0">{icon}</div>
      <div className="min-w-0 flex-1">
        <p className="text-xs text-tq-muted">{label}</p>
        <div className="text-sm font-medium text-tq-text truncate">{value}</div>
      </div>
    </div>
  )
}

export default function ProfileSection({ data, onRecharge }: Props) {
  const { profile } = data

  return (
    <div className="space-y-5">
      {/* Carte identité */}
      <div className="rounded-xl border border-tq-border bg-white p-5">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-12 h-12 rounded-full bg-tq-green flex items-center justify-center shrink-0">
            <User className="h-6 w-6 text-white" />
          </div>
          <div className="min-w-0">
            <p className="text-base font-bold text-tq-text truncate">
              {profile.full_name || 'Utilisateur'}
            </p>
            <p className="text-xs text-tq-muted truncate">{profile.email}</p>
          </div>
        </div>
        <div className="rounded-lg border border-tq-border">
          <Row icon={<Mail className="h-4 w-4" />} label="Email" value={profile.email || '—'} />
          <Row
            icon={<Shield className="h-4 w-4" />}
            label="Plan"
            value={
              <span className="flex items-center gap-2">
                <Badge variant="secondary" className="capitalize text-xs">
                  {profile.plan}
                </Badge>
                {profile.is_admin && (
                  <Badge className="bg-tq-green text-white border-0 text-xs">Admin</Badge>
                )}
              </span>
            }
          />
          <Row
            icon={<CalendarDays className="h-4 w-4" />}
            label="Membre depuis"
            value={formatDate(profile.created_at)}
          />
        </div>
      </div>

      {/* Solde + recharge */}
      <div className="rounded-xl border border-tq-border bg-white p-5">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-xs text-tq-muted mb-1">Solde de crédits</p>
            <p className="text-2xl font-bold text-tq-text">
              {profile.is_admin ? '∞' : formatCredits(profile.credits)}
            </p>
          </div>
          {!profile.is_admin && (
            <Button onClick={onRecharge} className="gap-1.5 bg-tq-green text-white hover:bg-tq-green/90">
              <CreditCard className="h-4 w-4" />
              Recharger
            </Button>
          )}
        </div>
      </div>

      {/* Apparence */}
      <div className="rounded-xl border border-tq-border bg-white p-5">
        <p className="text-xs font-semibold text-tq-text mb-3 uppercase tracking-wider">Apparence</p>
        <div className="flex items-center justify-between">
          <span className="text-sm text-tq-muted">Mode sombre</span>
          <ThemeToggle className="h-8 w-8 text-tq-muted hover:text-tq-text hover:bg-tq-hover" />
        </div>
      </div>
    </div>
  )
}
